import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CatalogCategory,
  CatalogDeleteImpact,
  CatalogCompatibility,
  CatalogComponent,
  CatalogComponentType,
  CatalogProduct,
  CatalogProductStructure,
  ConfigurationResponse,
  CreatePriceRuleRequest,
  CreatePricebookRequest,
  CreateProductRequest,
  CreateConfigurationRequest,
  ProductNodeCreateRequest,
  PriceResponse,
  RuleCreateRequest,
  UpdateSelectionsRequest,
  ValidateResponse
} from '../../configurator/configurator.models';

@Injectable({ providedIn: 'root' })
export class ConfiguratorApiService {
  private readonly http = inject(HttpClient);
  private readonly configurationBaseUrl = environment.configurationServiceBaseUrl;
  private readonly productBaseUrl = environment.productServiceBaseUrl;
  private readonly ruleBaseUrl = environment.ruleEngineBaseUrl;
  private readonly pricingBaseUrl = environment.pricingServiceBaseUrl;

  createConfiguration(payload: CreateConfigurationRequest): Observable<string> {
    return this.http.post<{ configurationId: string }>(`${this.configurationBaseUrl}/v1/configurations`, payload)
      .pipe(map((response) => response.configurationId));
  }

  getConfiguration(configurationId: string): Observable<ConfigurationResponse> {
    return this.http.get<ConfigurationResponse>(`${this.configurationBaseUrl}/v1/configurations/${configurationId}`);
  }

  updateSelections(configurationId: string, payload: UpdateSelectionsRequest): Observable<ConfigurationResponse> {
    return this.http.patch<ConfigurationResponse>(`${this.configurationBaseUrl}/v1/configurations/${configurationId}/selections`, payload);
  }

  validate(configurationId: string): Observable<ValidateResponse> {
    return this.http.post<ValidateResponse>(`${this.configurationBaseUrl}/v1/configurations/${configurationId}/validate`, {});
  }

  price(configurationId: string): Observable<PriceResponse> {
    return this.http.post<PriceResponse>(`${this.configurationBaseUrl}/v1/configurations/${configurationId}/price`, {});
  }

  submit(configurationId: string): Observable<ConfigurationResponse> {
    return this.http.post<ConfigurationResponse>(`${this.configurationBaseUrl}/v1/configurations/${configurationId}/submit`, {});
  }

  createProduct(payload: CreateProductRequest): Observable<string> {
    return this.http.post<{ productId: string }>(`${this.productBaseUrl}/v1/admin/products`, payload)
      .pipe(map((response) => response.productId));
  }

  createCategory(payload: { code: string; name: string }): Observable<string> {
    return this.http.post<{ categoryId: string }>(`${this.productBaseUrl}/v1/admin/catalog/categories`, payload)
      .pipe(map((response) => response.categoryId));
  }

  listCategories(): Observable<CatalogCategory[]> {
    return this.http.get<CatalogCategory[]>(`${this.productBaseUrl}/v1/admin/catalog/categories`);
  }

  listPublicCategories(): Observable<CatalogCategory[]> {
    return this.http.get<CatalogCategory[]>(`${this.productBaseUrl}/v1/catalog/categories`);
  }

  updateCategory(categoryId: string, payload: { code: string; name: string }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/categories/${categoryId}`, payload);
  }

  deleteCategory(categoryId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/categories/${categoryId}`);
  }

  createCatalogProduct(payload: { code: string; name: string; categoryId: string | null }): Observable<string> {
    return this.http.post<{ productId: string }>(`${this.productBaseUrl}/v1/admin/catalog/products`, payload)
      .pipe(map((response) => response.productId));
  }

  listCatalogProducts(): Observable<CatalogProduct[]> {
    return this.http.get<CatalogProduct[]>(`${this.productBaseUrl}/v1/admin/catalog/products`);
  }

  listPublicCatalogProducts(): Observable<CatalogProduct[]> {
    return this.http.get<CatalogProduct[]>(`${this.productBaseUrl}/v1/catalog/products`);
  }

  updateCatalogProduct(productId: string, payload: { code: string; name: string; categoryId: string | null }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/products/${productId}`, payload);
  }

  deleteCatalogProduct(productId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/products/${productId}`);
  }

  createComponentType(payload: { code: string; name: string }): Observable<string> {
    return this.http.post<{ componentTypeId: string }>(`${this.productBaseUrl}/v1/admin/catalog/component-types`, payload)
      .pipe(map((response) => response.componentTypeId));
  }

  listComponentTypes(): Observable<CatalogComponentType[]> {
    return this.http.get<CatalogComponentType[]>(`${this.productBaseUrl}/v1/admin/catalog/component-types`);
  }

  listPublicComponentTypes(): Observable<CatalogComponentType[]> {
    return this.http.get<CatalogComponentType[]>(`${this.productBaseUrl}/v1/catalog/component-types`);
  }

  updateComponentType(componentTypeId: string, payload: { code: string; name: string }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/component-types/${componentTypeId}`, payload);
  }

  deleteComponentType(componentTypeId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/component-types/${componentTypeId}`);
  }

  createComponent(payload: { componentTypeId: string; code: string; name: string; description: string | null }): Observable<string> {
    return this.http.post<{ componentId: string }>(`${this.productBaseUrl}/v1/admin/catalog/components`, payload)
      .pipe(map((response) => response.componentId));
  }

  listComponents(): Observable<CatalogComponent[]> {
    return this.http.get<CatalogComponent[]>(`${this.productBaseUrl}/v1/admin/catalog/components`);
  }

  listPublicComponents(): Observable<CatalogComponent[]> {
    return this.http.get<CatalogComponent[]>(`${this.productBaseUrl}/v1/catalog/components`);
  }

  updateComponent(componentId: string, payload: { componentTypeId: string; code: string; name: string; description: string | null }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/components/${componentId}`, payload);
  }

  deleteComponent(componentId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/components/${componentId}`);
  }

  createProductStructure(payload: { productId: string; componentTypeId: string; quantity: number; orderNo: number }): Observable<string> {
    return this.http.post<{ productStructureId: string }>(`${this.productBaseUrl}/v1/admin/catalog/product-structures`, payload)
      .pipe(map((response) => response.productStructureId));
  }

  listProductStructures(): Observable<CatalogProductStructure[]> {
    return this.http.get<CatalogProductStructure[]>(`${this.productBaseUrl}/v1/admin/catalog/product-structures`);
  }

  listPublicProductStructures(): Observable<CatalogProductStructure[]> {
    return this.http.get<CatalogProductStructure[]>(`${this.productBaseUrl}/v1/catalog/product-structures`);
  }

  updateProductStructure(productStructureId: string, payload: { productId: string; componentTypeId: string; quantity: number; orderNo: number }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/product-structures/${productStructureId}`, payload);
  }

  deleteProductStructure(productStructureId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/product-structures/${productStructureId}`);
  }

  createCompatibility(payload: { productId: string; componentId: string; required: boolean; defaultSelected: boolean }): Observable<string> {
    return this.http.post<{ compatibilityId: string }>(`${this.productBaseUrl}/v1/admin/catalog/compatibilities`, payload)
      .pipe(map((response) => response.compatibilityId));
  }

  listCompatibilities(): Observable<CatalogCompatibility[]> {
    return this.http.get<CatalogCompatibility[]>(`${this.productBaseUrl}/v1/admin/catalog/compatibilities`);
  }

  listPublicCompatibilities(): Observable<CatalogCompatibility[]> {
    return this.http.get<CatalogCompatibility[]>(`${this.productBaseUrl}/v1/catalog/compatibilities`);
  }

  updateCompatibility(compatibilityId: string, payload: { productId: string; componentId: string; required: boolean; defaultSelected: boolean }): Observable<void> {
    return this.http.put<void>(`${this.productBaseUrl}/v1/admin/catalog/compatibilities/${compatibilityId}`, payload);
  }

  deleteCompatibility(compatibilityId: string): Observable<void> {
    return this.http.delete<void>(`${this.productBaseUrl}/v1/admin/catalog/compatibilities/${compatibilityId}`);
  }

  getCatalogDeleteImpact(entityType: string, entityId: string): Observable<CatalogDeleteImpact> {
    return this.http.get<CatalogDeleteImpact>(
      `${this.productBaseUrl}/v1/admin/catalog/delete-impact/${entityType}/${entityId}`
    );
  }

  createProductVersion(productId: string): Observable<string> {
    return this.http.post<{ versionId: string }>(`${this.productBaseUrl}/v1/admin/products/${productId}/versions`, {})
      .pipe(map((response) => response.versionId));
  }

  createProductNode(productId: string, versionId: string, payload: ProductNodeCreateRequest): Observable<string> {
    return this.http.post<{ nodeId: string }>(
      `${this.productBaseUrl}/v1/admin/products/${productId}/versions/${versionId}/nodes`,
      payload
    ).pipe(map((response) => response.nodeId));
  }

  createRuleSet(productId: string, name: string): Observable<string> {
    return this.http.post<{ rulesetId: string }>(`${this.ruleBaseUrl}/v1/admin/rulesets`, { productId, name })
      .pipe(map((response) => response.rulesetId));
  }

  createRule(rulesetId: string, payload: RuleCreateRequest): Observable<string> {
    return this.http.post<{ ruleId: string }>(`${this.ruleBaseUrl}/v1/admin/rulesets/${rulesetId}/rules`, payload)
      .pipe(map((response) => response.ruleId));
  }

  publishRuleSet(rulesetId: string): Observable<void> {
    return this.http.post<void>(`${this.ruleBaseUrl}/v1/admin/rulesets/${rulesetId}/publish`, {});
  }

  createPricebook(payload: CreatePricebookRequest): Observable<string> {
    return this.http.post<{ pricebookId: string }>(`${this.pricingBaseUrl}/v1/admin/pricebooks`, payload)
      .pipe(map((response) => response.pricebookId));
  }

  createPriceRule(pricebookId: string, payload: CreatePriceRuleRequest): Observable<string> {
    return this.http.post<{ priceRuleId: string }>(`${this.pricingBaseUrl}/v1/admin/pricebooks/${pricebookId}/rules`, payload)
      .pipe(map((response) => response.priceRuleId));
  }

  publishPricebook(pricebookId: string): Observable<void> {
    return this.http.post<void>(`${this.pricingBaseUrl}/v1/admin/pricebooks/${pricebookId}/publish`, {});
  }
}
